import React, { useState, useEffect } from "react";
import { TeacherItem } from "./TeacherItem";
import supabase from "../../../utils/supabase";
import { Plus } from "lucide-react";
import AddTeacherModal from "../../modals/AddTeacherModal";
import ConfirmDeleteTeacherModal from "../../modals/ConfirmDeleteTeacherModal";
import { getTeachers } from "../../../utils/getTeachers";

interface SchoolOption {
  id: string;
  name: string;
  district_id: string;
}

export function Teachers() {
  const [teachers, setTeachers] = useState([]);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddTeacherOpen, setIsAddTeacherOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  async function fetchTeachers(search?: string) {
    setIsLoading(true);
    try {
      const { data, error } = await getTeachers({ search });

      if (error) {
        console.error("Failed to fetch teachers:", error);
        return;
      }

      setTeachers(data || []);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchSchools() {
    try {
      const { data, error } = await supabase
        .from("schools")
        .select("id, name, district_id");
      if (error) {
        console.error("Failed to fetch schools:", error);
        return;
      }
      setSchools(data || []);
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  }

  useEffect(() => {
    fetchSchools();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchTeachers(searchTerm);
      },
      searchTerm ? 500 : 0
    ); // No delay for initial load, 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Map school id to name
  const schoolMap: Record<string, string> = {};
  schools.forEach((s) => {
    schoolMap[s.id] = s.name;
  });

  const handleDeleteClick = (teacher: any) => {
    setTeacherToDelete({
      ...teacher,
      schoolName: schoolMap[teacher.school],
    });
    setIsDeleteModalOpen(true);
  };

  const handleTeacherDeleted = () => {
    // Refresh the teachers list
    fetchTeachers(searchTerm);
  };

  return (
    <div>
      {/* Add Teacher Modal */}
      <AddTeacherModal
        isOpen={isAddTeacherOpen}
        onClose={() => setIsAddTeacherOpen(false)}
        title="Add New Teacher"
        schools={schools}
        onSuccess={() => {
          setIsAddTeacherOpen(false);
          fetchTeachers(searchTerm);
        }}
      />

      <ConfirmDeleteTeacherModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTeacherToDelete(null);
        }}
        teacher={teacherToDelete}
        onTeacherDeleted={handleTeacherDeleted}
      />
      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teachers</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage all teachers and their assigned schools.
          </p>
        </div>
        <div className="space-x-2 flex gap-2">
          <button
            className="btn btn-small"
            onClick={() => setIsAddTeacherOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Teacher
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Teachers
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg"
                placeholder="Search by name or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    School
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading teachers...
                    </td>
                  </tr>
                ) : teachers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      {searchTerm
                        ? "No teachers found matching your search."
                        : "No teachers found."}
                    </td>
                  </tr>
                ) : (
                  teachers.map((teacher) => (
                    <TeacherItem
                      key={teacher.id}
                      teacher={{
                        id: teacher.id,
                        fullname: teacher.fullname,
                        email: teacher.email,
                        created_at: new Date(teacher.created_at).toISOString(),
                        school: teacher.school,
                      }}
                      schoolName={teacher.schoolName}
                      onDelete={handleDeleteClick}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
